import React, { useState, useEffect } from 'react';
import {
  Button,
  CircularProgress,
  IconButton,
  Typography,
  Box,
} from '@material-ui/core';
import BackIcon from '@material-ui/icons/ReplyOutlined';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import useStyles from './styles';
import TextAnswer from '../EditQuestionResponseComponents/TextAnswer';
import DropDownAnswer from '../EditQuestionResponseComponents/DropDownAnswer';
import { getQuestionResponseDetailById, putQuestionResponseData } from '../../../requests/api/question-response';
import { getProductsPerPage } from '../../../requests/api/product';
import Select from '../../../components/Select';
import Snackbar from '../../../components/Snackbar';
import SelectAnswer from '../EditQuestionResponseComponents/SelectAnswer';
import NumericAnswer from '../EditQuestionResponseComponents/NumericAnswer';
import DateTimeAnswer from '../EditQuestionResponseComponents/DateTimeAnswer';
import sleep from '../../../utils/sleep';
import MultimediaAnswer from '../EditQuestionResponseComponents/MultimediaAnswer';
import {
  LONG_ANSWER,
  SIMPLE_SELECTION,
  MULTIPLE_IMAGE_QUESTION,
  SHORT_ANSWER,
  IMAGE,
  MULTIPLE_SELECTION,
  DROP_DOWN_MENU,
  YES_NO_QUESTION,
  SATISFACTION,
  NPS_QUESTION,
  DATE,
  NUMERIC_ANSWER,
  SIGNATURE_QUESTION,
  BARCODE_QUESTION,
  DECIMAL_ANSWER,
  PRICE,
  VIDEO,
  AUDIO,
  AUDIO_BACKGROUND,
  TIME,
  FORMATTED_ANSWER,
} from '../../../utils/const/pageTypes';
import SignatureAnswer from '../EditQuestionResponseComponents/SignatureAnswer';

const TEXT_QUESTIONS = [
  LONG_ANSWER,
  SHORT_ANSWER,
  BARCODE_QUESTION,
  FORMATTED_ANSWER,
  'text',
];

const NUMERIC_QUESTIONS = [
  NUMERIC_ANSWER,
  DECIMAL_ANSWER,
  PRICE,

];

const MULTIMEDIA_QUESTIONS = [
  MULTIPLE_IMAGE_QUESTION,
  IMAGE,
  VIDEO,
  AUDIO,
  AUDIO_BACKGROUND,
];

const SELECTION_QUESTIONS = [
  SIMPLE_SELECTION,
  MULTIPLE_SELECTION,
  YES_NO_QUESTION,
];

const DATE_TIME_QUESTIONS = [
  DATE,
  TIME,
];

const EditQuestionResponse = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { taskResponseId, questionResponseId } = useParams();
  const history = useHistory();
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('');
  const [questionResponse, setQuestionResponse] = useState({});
  const [editableAnswer, setEditableAnswer] = useState('');
  const [editableOptions, setEditableOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [options, setOptions] = useState([{
    value: null,
    name: '-',
    type: null,
  }]);

  const isTextQuestion = (qrData) => TEXT_QUESTIONS.includes(qrData?.Question?.type);
  const isNumericQuestion = (qrData) => NUMERIC_QUESTIONS.includes(qrData?.Question?.type);
  const isMultimediaQuestion = (qrData) => MULTIMEDIA_QUESTIONS.includes(qrData?.Question?.type);
  const isDateTimeQuestion = (qrData) => DATE_TIME_QUESTIONS.includes(qrData?.Question?.type);
  const isSelectionQuestion = (qrData) => SELECTION_QUESTIONS.includes(qrData?.Question?.type);
  const isDropDownQuestion = (qrData) => qrData?.Question?.type === DROP_DOWN_MENU;
  const isDateQuestion = (qrData) => qrData?.Question?.type === DATE;
  const isTimeQuestion = (qrData) => qrData?.Question?.type === TIME;
  const isSatisfactionQuestions = (qrData) => qrData?.Question?.type === SATISFACTION;
  const isNPSQuestion = (qrData) => qrData?.Question?.type === NPS_QUESTION;
  const isAudioQuestion = (qrData) => [AUDIO, AUDIO_BACKGROUND]
    .includes(qrData?.Question?.type);
  const isVideoQuestion = (qrData) => qrData?.Question?.type === VIDEO;
  const isSignatureQuestion = (qrData) => qrData?.Question?.type === SIGNATURE_QUESTION;
  const itDoesHaveQCR = (qrData) => qrData?.QuestionChoiceResponses?.length > 0;
  const getType = (qrData) => qrData?.Question?.type;

  const getTranslatedType = (type) => {
    const typeTranslations = {
      'long answer': t('questionTypes.longAnswer'),
      'short answer': t('questionTypes.shortAnswer'),
      'drop down menu': t('questionTypes.dropDown'),
      'simple selection': t('questionTypes.simpleSelection'),
      'multiple selection': t('questionTypes.multipleSelection'),
      'numeric answer': t('questionTypes.numericAnswer'),
      'decimal answer': t('questionTypes.decimalAnswer'),
      'yes/no question': t('questionTypes.yesNoQuestion'),
      date: t('questionTypes.date'),
      time: t('questionTypes.time'),
      satisfaction: t('questionTypes.satisfaction'),
      'nps question': t('questionTypes.npsQuestion'),
      'multiple image question': t('questionTypes.multipleImageQuestion'),
      image: t('questionTypes.image'),
      'signature question': t('questionTypes.signatureQuestion'),
      'barcode question': t('questionTypes.barcodeQuestion'),
      video: t('questionTypes.video'),
      audio: t('questionTypes.audio'),
      'audio-background': t('questionTypes.audioBackground'),
      price: t('questionTypes.price'),
      content: t('questionTypes.content'),
      'welcome page': t('questionTypes.welcomePage'),
      'end page': t('questionTypes.endPage'),
      'resume page': t('questionTypes.resumePage'),
    };
    return typeTranslations[type] || type;
  };

  useEffect(() => {
    const setParams = (qrData) => {
      if (isTextQuestion(qrData) || isNumericQuestion(qrData) || isSignatureQuestion(qrData)) {
        setEditableAnswer(qrData?.value);
      }
      if (isDateQuestion(qrData)) {
        setEditableAnswer(new Date(`${qrData?.value}T00:00:00`));
      }
      if (isTimeQuestion(qrData)) {
        const timeValue = qrData?.value;
        if (timeValue) {
          const [hours, minutes] = timeValue.split(':');
          const timeDate = new Date();
          timeDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
          setEditableAnswer(timeDate);
        }
      }
      if (isDropDownQuestion(qrData)) {
        if (itDoesHaveQCR(qrData)) {
          setEditableAnswer(qrData.QuestionChoiceResponses[0].questionChoiceId);
        }
        setOptions([
          { value: '', name: '-' },
          ...qrData?.Question.QuestionChoices.map(
            (option) => ({ value: option.id, name: option.title }),
          ),
        ]);
      }
      if (isSelectionQuestion(qrData)) {
        setEditableAnswer(qrData?.value);
        if (itDoesHaveQCR(qrData)) {
          setEditableOptions(
            qrData.QuestionChoiceResponses.map(
              (choice) => choice.questionChoiceId,
            ),
          );
        }
        setOptions(
          qrData?.Question.QuestionChoices.map(
            (option) => ({ value: option.id, name: option.title, type: option.type }),
          ),
        );
      }
      if (isSatisfactionQuestions(qrData)) {
        setEditableAnswer([qrData?.value]);
        setOptions(
          Array.from({ length: qrData?.Question?.emoji_scale || 3 }, (_, i) => ({
            value: i + 1,
            name: (i + 1).toString(),
          })),
        );
      }
      if (isNPSQuestion(qrData)) {
        setEditableAnswer([qrData?.value]);
        setOptions(
          Array.from({ length: 10 }, (_, i) => ({
            value: i + 1,
            name: (i + 1).toString(),
          })),
        );
      }
      if (isMultimediaQuestion(qrData)) {
        if (isAudioQuestion(qrData)) {
          setEditableAnswer([{
            id: null,
            name: 'survey_audio',
            url: qrData.value,
            isExisting: true,
          }]);
        } else if (isVideoQuestion(qrData)) {
          setEditableAnswer(
            qrData.QuestionResponseVideos.map(
              (video) => ({
                id: video.id,
                name: video.videoKey,
                url: video.videoUrl,
                isExisting: true,
              }),
            ),
          );
        } else {
          setEditableAnswer(
            qrData.QuestionResponseImages.map(
              (img) => ({
                id: img.id,
                name: img.imageKey,
                url: img.imageUrl,
                isExisting: true,
              }),
            ),
          );
        }
      }
    };

    const fetchData = async () => {
      const response = await getQuestionResponseDetailById(questionResponseId);

      if (response.success) {
        const data = response.data.data.questionResponse;
        if (data.SurveyResponse.Survey.type === 'product') {
          const responseProduct = await getProductsPerPage(0, 5000, null, false);
          if (responseProduct.success) {
            const dataProduct = responseProduct.data.data.products;
            setProducts(dataProduct);
          }
        }
        setQuestionResponse(data);
        setSelectedProductId(data.productId || '');
        setParams(data);
      }
    };

    fetchData();
  }, [questionResponseId]);

  const createSelectOptions = () => {
    const options = products.map((product) => ({
      name: product.productName,
      value: product.id,
    }));

    options.unshift({
      name: 'Ninguno',
      value: '',
    });

    return options;
  };

  const handleSave = async () => {
    let body = {};
    if (
      isTextQuestion(questionResponse)
      || isNumericQuestion(questionResponse)
      || isSignatureQuestion(questionResponse)
    ) {
      body = {
        value: editableAnswer,
      };
    }
    if (isSelectionQuestion(questionResponse)) {
      const selectedOptions = options.filter((option) => editableOptions.includes(option.value));
      const isAnyOther = selectedOptions.find((option) => option.type === 'other');
      body = {
        value: isAnyOther ? editableAnswer : null,
        options: editableOptions,
      };
    } else if (
      isDropDownQuestion(questionResponse)
    ) {
      body = {
        value: [editableAnswer],
      };
    } else if (isSatisfactionQuestions(questionResponse)
      || isNPSQuestion(questionResponse)) {
      body = {
        value: Array.isArray(editableAnswer) ? editableAnswer[0] : null,
      };
    } else if (
      isDateQuestion(questionResponse)
    ) {
      body = {
        value: editableAnswer.toISOString().split('T')[0],
      };
    } else if (
      isTimeQuestion(questionResponse)
    ) {
      body = {
        value: `${editableAnswer.getHours()}:${editableAnswer.getMinutes()}`,
      };
    } else if (
      isMultimediaQuestion(questionResponse)
    ) {
      body = new FormData();

      if (questionResponse?.Question?.type === MULTIPLE_IMAGE_QUESTION) {
        body.append('useBuffer', 'true');
      }

      const keepImageIds = [];

      if (editableAnswer && editableAnswer.length > 0) {
        editableAnswer.forEach((file) => {
          if (file.isExisting && file.id) {
            keepImageIds.push(file.id);
          }
        });
      }

      // Agregar keepImageIds antes de los archivos para que busboy lo parsee primero
      body.append('keepImageIds', JSON.stringify(keepImageIds));

      // Agregar los archivos
      if (editableAnswer && editableAnswer.length > 0) {
        editableAnswer.forEach((file) => {
          if (!file.isExisting && file.file) {
            body.append('files', file.file);
          }
        });
      }
    }

    // Add selected product ID to body if available
    if (selectedProductId) {
      if (body instanceof FormData) {
        body.append('productId', selectedProductId);
      } else {
        body.productId = selectedProductId;
      }
    }

    setLoading(true);
    const response = await putQuestionResponseData(
      questionResponseId,
      body,
    );
    setLoading(false);
    if (response.success) {
      setMessage(t('common.savedSuccessfully'));
      setSeverity('success');
      sleep(1000).then(() => {
        history.push(`/responses/${taskResponseId}`);
      });
    } else {
      setMessage(t('common.savedUnsuccessfully'));
      setSeverity('error');
    }
  };

  const backButton = (
    <IconButton
      id="backButton"
      aria-label="back"
      className={classes.backButtonQuestionResponse}
      component={Link} to={`/responses/${taskResponseId}`}
    >
      <BackIcon fontSize="small" />
      <span>{t('groups.actionButtons.back')}</span>
    </IconButton>
  );

  const header = (
    <div className={classes.headerContainer}>
      <div className={classes.header}>
        {backButton}
        <Typography variant="h1" className={classes.title} gutterBottom>
          {t('editQuestionResponseView.title')}: {questionResponse?.Question?.title}
        </Typography>
      </div>
      <div className={classes.buttonsContainer}>
        <Button
          onClick={() => handleSave()}
        >
          {t('task.editDetail.save')}
        </Button>
      </div>
    </div>
  );

  const productSelector = products.length > 0 && (
    <Box mb={3}>
      <Select
        name="product"
        items={createSelectOptions()}
        label="Seleccionar Producto"
        value={selectedProductId}
        onChange={(e) => setSelectedProductId(e.target.value)}
        fullWidth
      />
    </Box>
  );

  const answerContent = (
    <>
      {isTextQuestion(questionResponse) && (
        <TextAnswer
          editableAnswer={editableAnswer}
          setEditableAnswer={setEditableAnswer}
        >
        </TextAnswer>
      )}
      {isDropDownQuestion(questionResponse) && (
        <DropDownAnswer
          editableAnswer={editableAnswer}
          setEditableAnswer={setEditableAnswer}
          options={options}
        >
        </DropDownAnswer>
      )}
      {
        (isSelectionQuestion(questionResponse)
          || isSatisfactionQuestions(questionResponse)
          || isNPSQuestion(questionResponse)
        ) && (
          <SelectAnswer
            editableAnswer={editableAnswer}
            setEditableAnswer={setEditableAnswer}
            editableOptions={editableOptions}
            setEditableOptions={setEditableOptions}
            options={options}
            type={getType(questionResponse)}
          />
        )}
      {isNumericQuestion(questionResponse) && (
        <NumericAnswer
          editableAnswer={editableAnswer}
          setEditableAnswer={setEditableAnswer}
        />
      )}
      {isMultimediaQuestion(questionResponse) && (
        <MultimediaAnswer
          editableAnswer={editableAnswer}
          setEditableAnswer={setEditableAnswer}
          type={getType(questionResponse)}
        />
      )}
      {isDateTimeQuestion(questionResponse) && (
        <DateTimeAnswer
          editableAnswer={editableAnswer}
          setEditableAnswer={setEditableAnswer}
          options={options}
          type={getType(questionResponse)}
        />
      )}
      {isSignatureQuestion(questionResponse) && (
        <SignatureAnswer
          editableAnswer={editableAnswer}
          setEditableAnswer={setEditableAnswer}
        />
      )}
    </>
  );

  const bodyContent = (
    <>
      <Typography variant="h2" className={classes.title} gutterBottom>
        {`${t('editQuestionResponseView.type')}: ${getTranslatedType(questionResponse?.Question?.type) || ''}`}
      </Typography>
      <Typography variant="h3" className={classes.description} gutterBottom>
        {questionResponse?.Question?.description?.replace(/<[^>]*>/g, '') || ''}
      </Typography>
      {productSelector}
      {answerContent}
    </>
  );

  return (
    <>
      {
        loading ? (
          <div className={classes.progress}>
            <CircularProgress />
            <h4>{t('users.userInfo.loading')}</h4>
          </div>
        )
          : (
            <div className={classes.content}>
              {header}
              {bodyContent}
            </div>
          )
      }
      <Snackbar
        open={message !== ''}
        message={message}
        severity={severity}
        onClose={() => setMessage('')}
      />
    </>
  );
};

export default EditQuestionResponse;
